// Create an account (`/signup`, ADR 0053, DESIGN.md "Sign-up and Get started"). Two steps in the
// sign-in frame: the details, then the emailed code and a password. Success lands on Admin
// Home with its Get started card. Off unless the server has signup on (`GET /v1/signup`). The
// details step asks for the terms (clickwrap, S12) and, when the server sends a site key, shows
// Cloudflare Turnstile (S7).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Checkbox, SignInLayout, Input as VdInput } from "@vd/ui";
import { Pencil } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { qk } from "../api/admin";
import { ApiError, errorMessage, setCsrfToken } from "../api/client";
import { type SignupConfig, signup, signupStatusQuery, type UseCase } from "../api/signup";
import { deviceInfo } from "../auth/device";
import { NewPasswordFields, passwordsMatch } from "../components/signin/NewPasswordFields";
import { Turnstile } from "../components/signin/Turnstile";
import { Button, HelpTip, Loading, Segmented } from "../components/ui";
import { Link, useNavigate } from "../lib/navigate";

export const Route = createFileRoute("/signup")({
  staticData: { crumb: { label: "Create your account", parents: [] } },
  component: SignUp,
});

const USE_CASES: { value: UseCase; label: string }[] = [
  { value: "phone", label: "Phone" },
  { value: "support", label: "Support" },
  { value: "sales", label: "Sales" },
];

function Alert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-ui text-danger-ink">
      {children}
    </p>
  );
}

function Label({ htmlFor, children, help }: { htmlFor: string; children: ReactNode; help?: ReactNode }) {
  return (
    <div className="flex items-center gap-1">
      <label htmlFor={htmlFor} className="font-medium text-caption text-ink-2">
        {children}
      </label>
      {help}
    </div>
  );
}

function SignUp() {
  const status = useQuery(signupStatusQuery);
  if (status.isPending) return <Loading />;
  if (!status.data)
    return (
      <SignInLayout>
        <div className="flex flex-col gap-4">
          <h1 className="font-display font-medium text-display">Create your account</h1>
          <p className="text-ink-2 text-ui">Sign-up isn't open yet.</p>
          <Link to="/signin" className="font-medium text-accent-ink text-ui">
            Sign in
          </Link>
        </div>
      </SignInLayout>
    );
  return <SignUpSteps config={status.data} />;
}

function SignUpSteps({ config }: { config: SignupConfig }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState<UseCase>("sales");
  const [agreed, setAgreed] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  // A new key mounts a fresh widget: a token is good for one start.
  const [widget, setWidget] = useState(0);
  const siteKey = config.turnstile_site_key;
  const [requestId, setRequestId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  const [field, setField] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  const fail = (err: unknown) => {
    const code = err instanceof ApiError ? err.code : "";
    const fieldOf: Record<string, string> = {
      invalid_email: "email",
      work_email_required: "email",
      name_required: "name",
      company_required: "company",
      bad_code: "code",
      code_expired: "code",
      weak_password: "password",
    };
    setField(fieldOf[code] ?? null);
    if (code === "terms_changed") {
      setAgreed(false);
      void qc.invalidateQueries({ queryKey: signupStatusQuery.queryKey });
    }
    if (code === "account_exists" || code === "signup_finished")
      setError(
        <>
          {errorMessage(err).replace(/ Sign in\.$/, "")}{" "}
          <Link to="/signin" className="font-medium underline">
            Sign in
          </Link>
        </>,
      );
    else setError(errorMessage(err));
  };

  const start = async (e: FormEvent) => {
    e.preventDefault();
    if (!agreed) return;
    setError(null);
    setField(null);
    if (siteKey && !token) {
      setError("Complete the check.");
      return;
    }
    setBusy(true);
    try {
      const r = await signup.start({
        email: email.trim(),
        name,
        company,
        use_case: useCase,
        terms_version: config.terms.version,
        privacy_version: config.privacy.version,
        ...(siteKey && token ? { turnstile_token: token } : {}),
      });
      setRequestId(r.request_id);
      setResendIn(30);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      if (siteKey) {
        setToken(null);
        setWidget((n) => n + 1);
      }
    }
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!requestId) return;
    setSubmitted(true);
    if (!passwordsMatch(password, confirm)) return;
    setBusy(true);
    setError(null);
    setField(null);
    try {
      const session = await signup.verify({
        request_id: requestId,
        code: code.replace(/\s/g, ""),
        password,
        confirm,
        client: "web",
        device: deviceInfo(),
      });
      setCsrfToken(session.csrf_token);
      qc.setQueryData(qk.me, session.me);
      await navigate({ to: "/admin" });
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!requestId || resendIn > 0) return;
    setResendIn(30);
    try {
      await signup.resend(requestId);
    } catch (err) {
      fail(err);
    }
  };

  if (!requestId)
    return (
      <SignInLayout>
        <form onSubmit={start} className="flex flex-col gap-4" noValidate>
          <h1 className="font-display font-medium text-display">Create your account</h1>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="su-email">Work email</Label>
            <VdInput
              id="su-email"
              type="email"
              autoComplete="email"
              autoFocus
              size="lg"
              value={email}
              invalid={field === "email"}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="su-name">Your name</Label>
            <VdInput
              id="su-name"
              autoComplete="name"
              size="lg"
              value={name}
              invalid={field === "name"}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="su-company">Company</Label>
            <VdInput
              id="su-company"
              autoComplete="organization"
              size="lg"
              value={company}
              invalid={field === "company"}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="su-use"
              help={
                <HelpTip
                  label="About Start with"
                  text="Picks your trial seats. You can add the others later."
                />
              }
            >
              Start with
            </Label>
            <div id="su-use" className="[&>*]:w-full">
              <Segmented label="Start with" value={useCase} options={USE_CASES} onChange={setUseCase} />
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="su-terms" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
            <label htmlFor="su-terms" className="text-ink-2 text-ui">
              I agree to the{" "}
              <a
                href={config.terms.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-accent-ink"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href={config.privacy.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-accent-ink"
              >
                Privacy Policy
              </a>
            </label>
          </div>
          {siteKey ? <Turnstile key={widget} siteKey={siteKey} onToken={setToken} /> : null}
          {error ? <Alert>{error}</Alert> : null}
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            busy={busy}
            disabled={busy || !agreed || !email.trim() || !name.trim() || !company.trim()}
          >
            Continue
          </Button>
          <p className="text-center text-caption text-ink-3">
            Have an account?{" "}
            <Link to="/signin" className="font-medium text-accent-ink">
              Sign in
            </Link>
          </p>
        </form>
      </SignInLayout>
    );

  return (
    <SignInLayout>
      <form onSubmit={create} className="flex flex-col gap-4" noValidate>
        <h1 className="font-display font-medium text-display">Check your email</h1>
        <div className="flex h-8 min-w-0 items-center gap-1 self-start rounded-pill bg-fill-2 pr-1 pl-3">
          <span className="truncate text-ink text-ui">{email.trim()}</span>
          <button
            type="button"
            onClick={() => {
              setRequestId(null);
              setCode("");
              setError(null);
              setField(null);
            }}
            aria-label="Change email"
            title="Change email"
            className="flex size-6 shrink-0 items-center justify-center rounded-pill text-ink-3 hover:bg-fill-3 hover:text-ink"
          >
            <Pencil className="size-3.5" />
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="su-code">Code</Label>
          <VdInput
            id="su-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            size="lg"
            maxLength={7}
            className="tabular-nums tracking-[0.3em]"
            value={code}
            invalid={field === "code"}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <NewPasswordFields
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          submitted={submitted}
          problem={field === "password" ? error : undefined}
          size="lg"
          idPrefix="su"
        />
        {error && field !== "password" ? <Alert>{error}</Alert> : null}
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          busy={busy}
          disabled={busy || code.replace(/\s/g, "").length !== 6 || !password || !confirm}
        >
          {busy ? "Creating" : "Create account"}
        </Button>
        <p className="text-center text-caption text-ink-3">
          {resendIn > 0 ? (
            <span className="tabular-nums">Send a new code in 0:{String(resendIn).padStart(2, "0")}</span>
          ) : (
            <button type="button" onClick={() => void resend()} className="font-medium text-accent-ink">
              Send a new code
            </button>
          )}
        </p>
      </form>
    </SignInLayout>
  );
}
